import React, { useRef, useState, useEffect } from "react";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	Typography,
	useTheme,
	alpha,
	CircularProgress,
	Chip,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PrintTwoToneIcon from "@mui/icons-material/PrintTwoTone";
import DownloadTwoToneIcon from "@mui/icons-material/DownloadTwoTone";
import ConfirmationNumberTwoToneIcon from "@mui/icons-material/ConfirmationNumberTwoTone";
import EventTwoToneIcon from "@mui/icons-material/EventTwoTone";
import PeopleAltTwoToneIcon from "@mui/icons-material/PeopleAltTwoTone";
import { useReactToPrint } from "react-to-print";
import { toPng } from "html-to-image";
import { saveAs } from "file-saver";
import { useApiQuery } from "src/hooks/useApiQuery";
import type { IVisitorDetails, IVisitorListItem, IVisitorTicket } from "../type/newVisitorType";
import TicketPassCard, { formatTicketDate } from "../TicketScan/TicketPassCard";

interface VisitorPassModalProps {
	open: boolean;
	onClose: () => void;
	visitor: IVisitorListItem | null;
}

const VisitorPassModal: React.FC<VisitorPassModalProps> = ({
	open,
	onClose,
	visitor,
}) => {
	const theme = useTheme();
	const passCardRef = useRef<HTMLDivElement>(null);
	const [isDownloading, setIsDownloading] = useState<boolean>(false);
	const [selectedTicketIndex, setSelectedTicketIndex] = useState<number>(0);

	const rawData = (visitor as any)?.data || visitor;
	const visitorId = rawData?.visitor_id || visitor?.visitor_id;

	// Fetch detailed visitor data to grab all existing tickets if needed
	const { data: visitorDetails, isLoading } = useApiQuery<IVisitorDetails>({
		endPoint: `/api/v1/admin/visitor/${visitorId}`,
		queryKeys: ["VisitorDetails", visitorId],
		enabled: Boolean(open && visitorId && !rawData?.ticket),
	});

	// Reset selected ticket index when modal opens or visitor changes
	useEffect(() => {
		if (open) {
			setSelectedTicketIndex(0);
		}
	}, [open, visitorId]);

	// Extract direct ticket object if present in response (e.g. { ticket_id, ticket_no, date, quantity, ... })
	const directTicket = rawData?.ticket;
	const detailTickets: IVisitorTicket[] = visitorDetails?.tickets || [];

	// Combine or format tickets list
	let tickets: any[] = [];
	if (directTicket && typeof directTicket === "object") {
		tickets = [
			directTicket,
			...detailTickets.filter(
				(t) => t.ticket_no && t.ticket_no !== directTicket.ticket_no
			),
		];
	} else if (detailTickets.length > 0) {
		tickets = detailTickets;
	} else if (Array.isArray(rawData?.tickets)) {
		tickets = rawData.tickets;
	}

	const currentTicket =
		tickets.length > 0
			? tickets[selectedTicketIndex] || tickets[0]
			: null;

	const passData = {
		id:
			currentTicket?.ticket_id ||
			currentTicket?.id ||
			rawData?.visitor_id,
		ticket_no:
			currentTicket?.ticket_no ||
			rawData?.ticket_no ||
			rawData?.visitor_no ||
			`VIS${rawData?.visitor_id || ""}`,
		visitor_name:
			rawData?.visitor_name ||
			rawData?.name ||
			currentTicket?.visitor_visitor_name ||
			visitorDetails?.visitor_name ||
			"",
		visitor_no:
			rawData?.visitor_no ||
			currentTicket?.visitor_no ||
			`${rawData?.visitor_id || ""}`,
		visitor_id: rawData?.visitor_id,
		quantity: currentTicket?.quantity || 1,
		payment_status:
			currentTicket?.payment_status ||
			rawData?.visitor_status ||
			visitorDetails?.visitor_status ||
			"Approved",
		date:
			currentTicket?.date ||
			currentTicket?.created_at ||
			rawData?.visitor_created_at ||
			visitorDetails?.visitor_created_at ||
			new Date().toISOString(),
		created_at:
			currentTicket?.created_at ||
			rawData?.visitor_created_at ||
			visitorDetails?.visitor_created_at,
	};

	const handlePrint = useReactToPrint({
		content: () => passCardRef.current,
		documentTitle: `VisitorPass-${passData.ticket_no || passData.visitor_no || "visitor"}`,
		pageStyle: `
			@page {
				size: auto;
				margin: 0mm;
			}
			@media print {
				html, body {
					margin: 0 !important;
					padding: 0 !important;
					-webkit-print-color-adjust: exact !important;
					print-color-adjust: exact !important;
				}
				body {
					display: flex !important;
					justify-content: center !important;
					align-items: center !important;
					min-height: 100vh !important;
					background: #ffffff !important;
					padding: 15mm 0 !important;
				}
				.ticket-pass-card-container,
				.visitor-ticket-pass-card {
					width: 100% !important;
					margin: 0 auto !important;
					box-shadow: none !important;
					page-break-inside: avoid !important;
					break-inside: avoid !important;
				}
			}
		`,
	});

	const handleDownload = async () => {
		if (!passCardRef.current) return;
		try {
			setIsDownloading(true);
			const dataUrl = await toPng(passCardRef.current, {
				quality: 1,
				pixelRatio: 3,
				backgroundColor: "#ffffff",
				cacheBust: true,
				includeQueryParams: true,
			});
			saveAs(
				dataUrl,
				`BTTF-Pass-${passData.ticket_no || passData.visitor_no || "visitor"}.png`
			);
		} catch (error) {
			console.error("Failed to download visitor pass:", error);
		} finally {
			setIsDownloading(false);
		}
	};

	if (!visitor) return null;

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="sm"
			fullWidth
			PaperProps={{
				sx: {
					borderRadius: 2.5,
					overflow: "hidden",
					boxShadow: "0 24px 48px rgba(0,0,0,0.18)",
				},
			}}
		>
			{/* Dialog Header */}
			<DialogTitle
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					py: 2,
					px: 3,
					bgcolor: alpha(theme.palette.primary.main, 0.04),
					borderBottom: "1px solid",
					borderColor: "divider",
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
					<Box
						sx={{
							width: 38,
							height: 38,
							borderRadius: "50%",
							bgcolor: alpha(theme.palette.primary.main, 0.12),
							color: "primary.main",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<ConfirmationNumberTwoToneIcon fontSize="small" />
					</Box>
					<Box>
						<Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
							Visitor Entry Pass
						</Typography>
						<Typography variant="caption" color="text.secondary">
							{rawData?.visitor_name || rawData?.name || visitor?.visitor_name} •{" "}
							{rawData?.visitor_phone || rawData?.phone || rawData?.visitor_no || visitor?.visitor_phone}
						</Typography>
					</Box>
				</Box>

				<IconButton
					onClick={onClose}
					size="small"
					sx={{
						color: "text.secondary",
						"&:hover": { bgcolor: "action.hover", color: "text.primary" },
					}}
				>
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>

			{/* Dialog Content with Pass Card and Ticket Selector */}
			<DialogContent
				sx={{
					py: 2.5,
					px: { xs: 2, sm: 3 },
					bgcolor: "#f8fafc",
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					minHeight: 460,
				}}
			>
				{isLoading ? (
					<Box sx={{ py: 8, textAlign: "center" }}>
						<CircularProgress size={36} color="primary" />
						<Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
							Loading visitor tickets...
						</Typography>
					</Box>
				) : (
					<>
						{/* Compact Multi-Ticket Selector */}
						{tickets.length > 1 && (
							<Box
								sx={{
									width: "100%",
									maxWidth: 380,
									mb: 1.5,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									flexWrap: "wrap",
									gap: 0.8,
									bgcolor: alpha(theme.palette.primary.main, 0.04),
									py: 0.75,
									px: 1.2,
									borderRadius: 2,
									border: "1px solid",
									borderColor: alpha(theme.palette.primary.main, 0.12),
								}}
							>
								<Typography
									variant="caption"
									fontWeight={700}
									color="text.secondary"
									sx={{ fontSize: "0.72rem", mr: 0.2 }}
								>
									Ticket ({tickets.length}):
								</Typography>

								<Box
									sx={{
										display: "flex",
										gap: 0.6,
										flexWrap: "wrap",
										justifyContent: "center",
									}}
								>
									{tickets.map((t, idx) => {
										const isSelected = selectedTicketIndex === idx;
										const labelText = t.ticket_no
											? `${t.ticket_no}${Number(t.quantity) > 1 ? ` (${t.quantity}p)` : ""
											}`
											: `#${idx + 1}`;

										return (
											<Chip
												key={t.id || idx}
												clickable
												size="small"
												onClick={() => setSelectedTicketIndex(idx)}
												label={labelText}
												color={isSelected ? "primary" : "default"}
												variant={isSelected ? "filled" : "outlined"}
												sx={{
													height: 24,
													fontSize: "0.7rem",
													fontWeight: isSelected ? 700 : 600,
													cursor: "pointer",
													borderRadius: 1,
													transition: "all 0.15s ease",
													boxShadow: isSelected
														? `0 1px 4px ${alpha(
															theme.palette.primary.main,
															0.35
														)}`
														: "none",
												}}
											/>
										);
									})}
								</Box>
							</Box>
						)}

						{/* Single Ticket Info Badge */}
						{tickets.length === 1 && (
							<Box sx={{ mb: 1 }}>
								<Chip
									icon={<ConfirmationNumberTwoToneIcon fontSize="small" />}
									label={`Ticket: ${currentTicket?.ticket_no || `#${currentTicket?.id}`
										}${Number(currentTicket?.quantity) > 1
											? ` (${currentTicket?.quantity} Persons)`
											: ""
										}`}
									size="small"
									color="primary"
									variant="outlined"
									sx={{
										height: 24,
										fontWeight: 600,
										fontSize: "0.7rem",
										bgcolor: alpha(theme.palette.primary.main, 0.05),
									}}
								/>
							</Box>
						)}

						{/* Pass Card Preview */}
						<Box
							sx={{
								width: "100%",
								display: "flex",
								justifyContent: "center",
								my: 0.5,
							}}
						>
							<TicketPassCard
								ref={passCardRef}
								ticket={passData}
								width={320}
							/>
						</Box>
					</>
				)}
			</DialogContent>

			<Divider />

			{/* Dialog Actions */}
			<DialogActions
				sx={{
					p: 2,
					px: 3,
					gap: 1.5,
					justifyContent: "space-between",
					bgcolor: "#ffffff",
				}}
			>
				<Button
					variant="outlined"
					color="inherit"
					onClick={onClose}
					size="small"
					sx={{ fontWeight: 600 }}
				>
					Close
				</Button>

				<Box sx={{ display: "flex", gap: 1.5 }}>
					<Button
						variant="outlined"
						color="primary"
						startIcon={
							isDownloading ? (
								<CircularProgress size={16} color="inherit" />
							) : (
								<DownloadTwoToneIcon />
							)
						}
						onClick={handleDownload}
						disabled={isDownloading || isLoading}
						size="small"
						sx={{ fontWeight: 600 }}
					>
						Download PNG
					</Button>

					<Button
						variant="contained"
						color="primary"
						startIcon={<PrintTwoToneIcon />}
						onClick={() => handlePrint()}
						disabled={isLoading}
						size="small"
						sx={{ fontWeight: 700, px: 2.5 }}
					>
						Print Pass
					</Button>
				</Box>
			</DialogActions>
		</Dialog>
	);
};

export default VisitorPassModal;
